import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, MessageSquare } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import type { UIChecklistItem } from "@/packages/types";
import { NotesContent } from "./ChecklistNotesSection";

const MAX_PREVIEW_CHARS = 120;

export function ChecklistNotesPreview({ item }: { item: UIChecklistItem }) {
  const { t } = useTranslation("audits");
  const [expanded, setExpanded] = useState(false);

  const notes = item.notes?.trim() ? item.notes : null;
  const preview = notes
    ? notes.length > MAX_PREVIEW_CHARS
      ? `${notes.slice(0, MAX_PREVIEW_CHARS)}...`
      : notes
    : null;

  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-page/60"
      >
        <MessageSquare size={14} className="shrink-0 text-muted" />
        <span className="text-[12px] font-medium text-secondary">
          {t("checklist.overview.notes")}
        </span>

        {preview ? (
          <span className="ml-1 flex-1 truncate text-[12px] text-muted">
            {preview}
          </span>
        ) : (
          <span className="ml-1 flex-1 italic text-[12px] text-muted/60">
            {t("checklist.overview.noNotes")}
          </span>
        )}

        <motion.span
          animate={{ rotate: expanded ? 90 : 0 }}
          transition={{ duration: 0.15 }}
          className="shrink-0"
        >
          <ChevronRight size={14} className="text-muted" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3">
              <NotesContent item={item} />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
